import { HHBox, HHStack } from '@hinge-health/react-component-library';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { FileProcessingStatus } from '../domain/enums';
import { IN_FLIGHT_STATUSES } from '../types';
import {
  getStatusStyle,
  getToneColors,
  StatusTone,
} from '../utils/status-style.utils';

export const OUTCOME_STRIP_TEST_ID = 'pdg-outcome-strip';
/** Label for a provider whose recent files could not be fetched at all. */
export const OUTCOME_STRIP_UNAVAILABLE_LABEL = 'Recent files unavailable';
export const outcomeCellTestId = (index: number): string =>
  `pdg-outcome-cell-${index}`;

const DEFAULT_CAPACITY = 10;
const CELL_WIDTH_PX = 10;
const CELL_HEIGHT_PX = 18;

type CellTone = StatusTone | 'empty';

const countBy = (
  outcomes: FileProcessingStatus[],
  statuses: FileProcessingStatus[],
): number => outcomes.filter(outcome => statuses.includes(outcome)).length;

/** Text alternative for the strip, e.g. "Last 4 files: 2 completed, 1 rejected, 1 in flight". */
export const outcomeStripLabel = (outcomes: FileProcessingStatus[]): string => {
  if (outcomes.length === 0) {
    return 'No files yet';
  }

  const parts: Array<[number, string]> = [
    [countBy(outcomes, [FileProcessingStatus.Completed]), 'completed'],
    [countBy(outcomes, [FileProcessingStatus.CompletedWithErrors]), 'partial'],
    [countBy(outcomes, [FileProcessingStatus.Failed]), 'failed'],
    [countBy(outcomes, [FileProcessingStatus.Rejected]), 'rejected'],
    [countBy(outcomes, IN_FLIGHT_STATUSES), 'in flight'],
    [countBy(outcomes, [FileProcessingStatus.Cancelled]), 'cancelled'],
  ];
  const described = parts
    .filter(([count]) => count > 0)
    .map(([count, word]) => `${count} ${word}`)
    .join(', ');
  const noun = outcomes.length === 1 ? 'file' : 'files';

  return `Last ${outcomes.length} ${noun}: ${described}`;
};

interface OutcomeStripProps {
  /** Newest first, as `ProviderSummary.recentOutcomes` provides. */
  outcomes: FileProcessingStatus[];
  capacity?: number;
  testId?: string;
  /**
   * Replaces the computed `aria-label`. For a provider whose probe failed,
   * an empty strip means "we do not know", not "no files yet".
   */
  label?: string;
}

/**
 * Ten small cells, oldest on the left, newest on the right, coloured by
 * status tone. The cells are decoration; the `aria-label` carries the
 * meaning, the same way the batch heatmap does it.
 */
export const OutcomeStrip = ({
  outcomes,
  capacity = DEFAULT_CAPACITY,
  testId = OUTCOME_STRIP_TEST_ID,
  label,
}: OutcomeStripProps): JSX.Element => {
  const newestFirst = outcomes.slice(0, capacity);
  const oldestFirst = [...newestFirst].reverse();
  const emptySlots = capacity - oldestFirst.length;
  const tones: CellTone[] = [
    ...Array.from({ length: emptySlots }, (): CellTone => 'empty'),
    ...oldestFirst.map(outcome => getStatusStyle(outcome).tone),
  ];

  return (
    <HHStack
      direction="row"
      gap={0.5}
      role="img"
      aria-label={label ?? outcomeStripLabel(newestFirst)}
      data-testid={testId}
    >
      {tones.map((tone, index) => (
        <HHBox
          key={index}
          aria-hidden="true"
          data-testid={outcomeCellTestId(index)}
          data-tone={tone}
          sx={(theme: Theme): SystemStyleObject<Theme> => ({
            width: CELL_WIDTH_PX,
            height: CELL_HEIGHT_PX,
            borderRadius: '2px',
            backgroundColor:
              tone === 'empty'
                ? theme.palette.divider
                : getToneColors(theme, tone).fg,
            opacity: tone === 'empty' ? 0.5 : 1,
          })}
        />
      ))}
    </HHStack>
  );
};
